import { Modal, ModalProps } from "@/components/molecules";
import { Company } from "@/types/company";
import { JobPost } from "@/types/jobPost";
import { TextField } from "@/components/atoms";
import { useEffect, useState } from "react";
import { useFlagCompanyError, useFlagJobPostError } from "@/hooks";
import { Typography } from "@mui/material";

export interface FlagErrorModalProps extends ModalProps {
  type: "jobPost" | "company";
  jobPost?: JobPost;
  company?: Company;
}

export const FlagErrorModal = ({
  open,
  type,
  jobPost,
  company,
  header,
  subheader,
  logoUrl,
  onCloseButtonClick,
}: FlagErrorModalProps) => {
  const {
    flagJobPostError,
    loading: flagJobLoading,
    error: flagJobError,
    success: flagJobSuccess,
  } = useFlagJobPostError();
  const {
    flagCompanyError,
    loading: flagCompanyLoading,
    error: flagCompError,
    success: flagCompanySuccess,
  } = useFlagCompanyError();

  const [errorDetails, setErrorDetails] = useState("");

  const [snackbar, setSnackbar] = useState({
    state: false,
    msg: "",
    severity: "success" as "success" | "error",
  });

  const componentName =
    type === "jobPost" ? "flag-job-post-error" : "flag-company-error";

  const loading = flagJobLoading || flagCompanyLoading;
  const error = flagJobError || flagCompError;
  const success = flagJobSuccess || flagCompanySuccess;

  const handleSubmit = async () => {
    if (type === "jobPost" && jobPost) {
      await flagJobPostError(jobPost.id, errorDetails);
    }

    if (type === "company" && company) {
      await flagCompanyError(company.id, errorDetails);
    }
  };

  useEffect(() => {
    if (success) {
      setSnackbar({
        state: true,
        msg: success,
        severity: "success",
      });
      setErrorDetails("");

      setTimeout(() => {
        onCloseButtonClick && onCloseButtonClick();
      }, 2500);
    } else if (error) {
      setSnackbar({
        state: true,
        msg: error,
        severity: "error",
      });
    }
  }, [success, error]);

  return (
    <Modal
      componentName={componentName}
      open={open}
      header={header}
      subheader={subheader}
      logoUrl={logoUrl ?? undefined}
      loading={loading}
      error={error}
      onCloseButtonClick={onCloseButtonClick}
      snackbar={{
        ...snackbar,
        onClose: () => setSnackbar(prev => ({ ...prev, state: false })),
      }}
      renderButtonSet
      rightButtonText="Send"
      handleRightButtonClick={handleSubmit}
      disableButtonSet={!!success}
    >
      {!success ? (
        <TextField
          componentName={`${componentName}-error-details`}
          placeholder="Message"
          variant="outlined"
          value={errorDetails}
          label="Message"
          onChange={e => setErrorDetails(e.target.value)}
          required
          multiline
          rows={4}
          sx={{
            mt: 2,
          }}
          fullWidth
        />
      ) : (
        <Typography variant="h3" sx={{ textAlign: "center", mt: 2 }}>
          Thank you for your help!
        </Typography>
      )}
    </Modal>
  );
};
